import { observer } from 'mobx-react-lite';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import objectStore from '../../stores/objectStore';
import filterStore from '../../stores/filterStore';
import KeywordsFilterField from './KeywordsFilterField';
import { Box, Checkbox, FormControlLabel } from '@mui/material';

import requestStore from '../../stores/requestStore';
import logger from '../../utils/logger';

function ExtraFilters({ categoryId, source, onClose }) {
  let workStore = filterStore;

  if (source === 'objectpage') {
    workStore = filterStore;
  }
  if (source === 'request') {
    workStore = requestStore;
  }

  const currentJsonFile = objectStore.jsonfiles.find((file) => file?.type?.id === categoryId);
  if (!currentJsonFile) return null;

  // Только поля с extraFilter === true
  const extraFields = Object.entries(currentJsonFile).filter(([_, value]) => value?.extraFilter);

  // Удобный хелпер: поставить в стор
  const setExtraFilterValue = (fieldJson, value) => {
    workStore.extraFilter[fieldJson] = value;
  };

  // Удалить из стора (кнопка "Неважно")
  const removeExtraFilterKey = (fieldJson) => {
    delete workStore.extraFilter[fieldJson];
  };

  // Функция для замены запятых на точки в числовых полях
  const handleNumberInput = (value) => {
    if (typeof value === 'string') {
      return value.replace(/,/g, '.');
    }
    return value;
  };

  // ---- NUMBER INTERVAL
  const handleNumberIntervalChange = (fieldJson, boundary, newValue) => {
    const processedValue = handleNumberInput(newValue);
    const oldValue = workStore.extraFilter[fieldJson] || {};
    setExtraFilterValue(fieldJson, {
      ...oldValue,
      [boundary]: processedValue,
    });
  };

  // ---- BUTTONS_FILTER
  const handleButtonClick = (fieldJson, clickedId, isMultiple) => {
    // Если пользователь кликнул «Неважно»:
    if (clickedId === 'any') {
      removeExtraFilterKey(fieldJson);
      return;
    }

    // Если пользователь кликает по конкретной опции ремонта
    const oldVal = workStore.extraFilter[fieldJson];

    if (isMultiple) {
      // multiple => массив
      let arr = Array.isArray(oldVal) ? oldVal : [];
      // есть ли уже этот ID?
      if (arr.includes(clickedId)) {
        // убираем (повторное нажатие – снимаем выбор)
        arr = arr.filter((id) => id !== clickedId);
        // если массив стал пустым, удаляем ключ → «Неважно»
        arr.length > 0 ? setExtraFilterValue(fieldJson, arr) : removeExtraFilterKey(fieldJson);
      } else {
        // добавляем
        setExtraFilterValue(fieldJson, [...arr, clickedId]);
      }
    } else {
      // single => просто одно число
      // если нажали второй раз на уже выбранный ID — сбрасываем (Неважно)
      if (oldVal === clickedId) {
        removeExtraFilterKey(fieldJson);
      } else {
        setExtraFilterValue(fieldJson, clickedId);
      }
    }
  };

  const renderField = (field) => {
    switch (field.controlType) {
      case 'number_interval_filter': {
        // Получаем текущие значения из стора
        const fieldJson = field.fieldJson;
        const currentVal = workStore.extraFilter[fieldJson] || {};

        return (
          <div style={{ display: 'flex', gap: '8px' }}>
            <TextField
              type="text"
              size="small"
              placeholder={field.placeholderFrom || 'от'}
              value={currentVal.from || ''}
              onChange={(e) => handleNumberIntervalChange(field.fieldJson, 'from', e.target.value)}
              inputProps={{
                style: { textAlign: 'right' },
              }}
              onKeyPress={(e) => {
                const charCode = e.which || e.keyCode;
                const isNumberOrDot =
                  (charCode >= 48 && charCode <= 57) || charCode === 46 || charCode === 44;
                if (!isNumberOrDot) e.preventDefault();
                if (charCode === 44) {
                  e.preventDefault();
                  e.target.value = e.target.value + '.';
                }
              }}
            />
            <TextField
              type="text"
              size="small"
              placeholder={field.placeholderTo || 'до'}
              value={currentVal.to || ''}
              onChange={(e) => handleNumberIntervalChange(field.fieldJson, 'to', e.target.value)}
              inputProps={{
                style: { textAlign: 'right' },
              }}
              onKeyPress={(e) => {
                const charCode = e.which || e.keyCode;
                const isNumberOrDot =
                  (charCode >= 48 && charCode <= 57) || charCode === 46 || charCode === 44;
                if (!isNumberOrDot) e.preventDefault();
                if (charCode === 44) {
                  e.preventDefault();
                  e.target.value = e.target.value + '.';
                }
              }}
            />
          </div>
        );
      }
      case 'floor_filter': {
        const fieldJson = field.fieldJson; // "floor"
        // Если в extraFilter.floor ещё ничего нет, используем пустой объект
        const currentVal = workStore.extraFilter[fieldJson] || {};
        // Чекбоксы
        const checkboxOptions = field.checkboxOptions || [];

        // Функция обновления
        const setFloorValue = (newPartial) => {
          workStore.extraFilter[fieldJson] = {
            ...currentVal,
            ...newPartial,
          };
        };

        const handleFromChange = (val) => {
          setFloorValue({ from: handleNumberInput(val) });
        };
        const handleToChange = (val) => {
          setFloorValue({ to: handleNumberInput(val) });
        };

        const handleCheckboxChange = (id, checked) => {
          // Если, например, при включении "only_last" хотим выключить остальные
          if (id === 'only_last' && checked) {
            setFloorValue({
              only_last: true,
              not_first: false,
              not_last: false,
            });
          }
          // И наоборот, если включили "not_first" или "not_last", сбрасываем "only_last"
          else if ((id === 'not_first' || id === 'not_last') && checked) {
            setFloorValue({
              ...currentVal,
              [id]: true,
              only_last: false,
            });
          } else {
            // Обычное обновление
            setFloorValue({ [id]: checked });
          }
        };

        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {/* Поля "от" и "до" */}
            <div style={{ display: 'flex', gap: '8px' }}>
              <TextField
                type="text"
                size="small"
                placeholder={field.placeholderFrom || 'от'}
                value={currentVal.from || ''}
                onChange={(e) => handleFromChange(e.target.value)}
                inputProps={{
                  style: { textAlign: 'right' },
                }}
                onKeyPress={(e) => {
                  const charCode = e.which || e.keyCode;
                  const isNumberOrDot =
                    (charCode >= 48 && charCode <= 57) || charCode === 46 || charCode === 44;
                  if (!isNumberOrDot) e.preventDefault();
                  if (charCode === 44) {
                    e.preventDefault();
                    e.target.value = e.target.value + '.';
                  }
                }}
              />
              <TextField
                type="text"
                size="small"
                placeholder={field.placeholderTo || 'до'}
                value={currentVal.to || ''}
                onChange={(e) => handleToChange(e.target.value)}
                inputProps={{
                  style: { textAlign: 'right' },
                }}
                onKeyPress={(e) => {
                  const charCode = e.which || e.keyCode;
                  const isNumberOrDot =
                    (charCode >= 48 && charCode <= 57) || charCode === 46 || charCode === 44;
                  if (!isNumberOrDot) e.preventDefault();
                  if (charCode === 44) {
                    e.preventDefault();
                    e.target.value = e.target.value + '.';
                  }
                }}
              />
            </div>

            {/* Чекбоксы */}
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              {checkboxOptions.map((cb) => {
                const isChecked = !!currentVal[cb.id]; // true/false
                return (
                  <FormControlLabel
                    key={cb.id}
                    control={
                      <Checkbox
                        checked={isChecked}
                        onChange={(e) => handleCheckboxChange(cb.id, e.target.checked)}
                      />
                    }
                    label={cb.value}
                  />
                );
              })}
            </Box>
          </div>
        );
      }

      case 'keywords_filter':
        return (
          <KeywordsFilterField field={field} currentJsonFile={currentJsonFile} source={source} />
        );

      case 'buttons_filter': {
        const originalFieldKey = field.fieldJson; // например "renovation"
        const originalField = currentJsonFile[originalFieldKey];
        if (!originalField?.options) return null;

        // Определяем, «активно» ли у нас сейчас что-то кроме «Неважно»
        const currentVal = workStore.extraFilter[field.fieldJson];
        const isMultiple = field.multiple;

        // Признак «Неважно» активен — если ключа нет в store
        const isNoFilter = currentVal === undefined;

        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {/* Кнопка "Неважно" */}
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <Button
                variant={isNoFilter ? 'contained' : 'outlined'}
                onClick={() => handleButtonClick(field.fieldJson, 'any', isMultiple)}
                sx={{ textTransform: 'none' }}>
                Неважно
              </Button>

              {/* Список кнопок по options */}
              {originalField.options.map((opt) => {
                let isSelected = false;
                if (!isNoFilter) {
                  // Если «Неважно» не нажато, смотрим, что именно выбрано
                  if (isMultiple) {
                    // multiple => массив
                    isSelected = Array.isArray(currentVal) ? currentVal.includes(opt.id) : false;
                  } else {
                    // single => число
                    isSelected = currentVal === opt.id;
                  }
                }

                return (
                  <Button
                    key={opt.id}
                    variant={isSelected ? 'contained' : 'outlined'}
                    onClick={() => handleButtonClick(field.fieldJson, opt.id, isMultiple)}
                    sx={{ textTransform: 'none' }}>
                    {opt.value}
                  </Button>
                );
              })}
            </div>
          </div>
        );
      }

      // Пример кода в ExtraFilters
      case 'buttons_filter_pre': {
        // field.options у нас примерно:
        // [ { "id": 2.5, "value": "От 2,5 м" }, { "id": 2.7, "value": "От 2,7 м" }, ... ]
        // Здесь "id" = само число, которое сразу хотим послать на сервер.
        const { options, fieldJson, multiple } = field;
        if (!options) return null;

        // Узнаём текущее значение, например ceiling_height = 3
        const currentVal = workStore.extraFilter[fieldJson];
        // Считаем «Неважно», если ключа нет
        const isNoFilter = currentVal === undefined;

        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {/* Кнопка "Неважно" */}
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <Button
                variant={isNoFilter ? 'contained' : 'outlined'}
                onClick={() => {
                  delete workStore.extraFilter[fieldJson];
                }}
                sx={{ textTransform: 'none' }}>
                Неважно
              </Button>

              {/* Рендерим список предустановленных значений */}
              {options.map((opt) => {
                // opt.id = 3, 3.5, 4...
                let isSelected = false;
                if (!isNoFilter) {
                  if (multiple) {
                    // Если вдруг хотим multiple, тогда currentVal – это массив
                    isSelected = Array.isArray(currentVal) ? currentVal.includes(opt.id) : false;
                  } else {
                    // single
                    isSelected = currentVal === opt.id;
                  }
                }

                return (
                  <Button
                    key={opt.id}
                    sx={{ textTransform: 'none' }}
                    variant={isSelected ? 'contained' : 'outlined'}
                    onClick={() => {
                      if (multiple) {
                        // Логика для множественного выбора
                        let arr = Array.isArray(currentVal) ? currentVal : [];
                        if (arr.includes(opt.id)) {
                          // повторный клик – убрать
                          arr = arr.filter((x) => x !== opt.id);
                          // если в итоге пусто, = «Неважно»
                          arr.length
                            ? (workStore.extraFilter[fieldJson] = arr)
                            : delete workStore.extraFilter[fieldJson];
                        } else {
                          // добавить
                          workStore.extraFilter[fieldJson] = [...arr, opt.id];
                        }
                      } else {
                        // single
                        if (currentVal === opt.id) {
                          // повторный клик – сброс в «Неважно»
                          delete workStore.extraFilter[fieldJson];
                        } else {
                          workStore.extraFilter[fieldJson] = opt.id;
                        }
                      }
                    }}>
                    {opt.value}
                  </Button>
                );
              })}
            </div>
          </div>
        );
      }

      default:
        return null;
    }
  };

  return (
    // Главный контейнер без ограничения высоты
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      {/* Контейнер для верхней кнопки "Закрыть" - делаем её липкой */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          mb: 2,
          position: 'sticky',
          top: 0,
          zIndex: 10,
          bgcolor: 'background.paper',
          py: 1,
        }}>
        <Button onClick={onClose} variant="outlined" color="primary" size="small">
          Закрыть
        </Button>
      </Box>

      {/* Центральный контент с прокруткой */}
      <Box
        sx={{
          maxHeight: 'calc(90vh - 80px)',
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          pb: 3,
        }}>
        {import.meta.env.VITE_SHOW_COMPONENT_NAME === 'true' && (
          <div onClick={() => logger.log('ExtraFilters Render')}>% ExtraFilters %</div>
        )}

        {extraFields.map(([key, field]) => (
          <div key={key} style={{ marginBottom: '1rem' }}>
            <div style={{ marginBottom: '0.5rem' }}>{field.label}</div>
            {renderField(field)}
          </div>
        ))}

        {/* 	<div>
					<pre>{JSON.stringify(workStore.extraFilter, null, 2)}</pre>
				</div> */}
      </Box>
    </Box>
  );
}

export default observer(ExtraFilters);
